import { Box, Typography, Tooltip } from '@mui/material';
import { tooltipSxElevated } from '../shared/jernyTooltipStyling';
import { emsToDisplay } from '../../services/utils/DateFormat';
import userSettingsEngine from '../../controllers/UserSettings/UserSettingsEngine';
import { DAY_NAMES, MONTH_NAMES, buildDayCells, isToday, getEventsForDay, truncateLabel } from './calendarHelpers';

/**
 * Renders the tooltip content for a day with events — a standard Jerny
 * titlebar (date formatted via the user's "dateFormat" setting, the plain
 * date-only one under Regional settings, not contentPanel.chipDateTimeFormat
 * which always includes a time component — would show a meaningless 00:00
 * since this titlebar represents the whole day, not one event) on top, then
 * one "HH:MM - label" line per event below (label truncated to 45 chars).
 * The titlebar bleeds out via negative margin to cancel the tooltip's own
 * padding (2px 6px from jernyTooltipStyling), so it spans edge-to-edge like
 * every other Jerny titlebar instead of sitting inset.
 * @param {object} props
 * @param {number} props.year
 * @param {number} props.month - 0-based
 * @param {number} props.day
 * @param {CalEventObj[]} props.events - events for this day, sorted by ems
 */
function DayEventsTooltip({ year, month, day, events }) {
    const dayEms = new Date(year, month, day).getTime();
    const dateLabel = emsToDisplay(dayEms, userSettingsEngine.resolve('dateFormat'));

    return (
        <Box sx={{ minWidth: 160 }}>
            <Box
                sx={{
                    margin: '-2px -6px 4px -6px',
                    padding: '3px 6px',
                    background: '#05407a',
                    borderRadius: '3px 3px 0 0',
                    borderBottom: '0.5px solid #294766',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#ffffff',
                }}
            >
                {dateLabel}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {events.map((e) => (
                    <span key={e.rk}>
                        {e.time} - {truncateLabel(e.label)}
                    </span>
                ))}
            </Box>
        </Box>
    );
}

/**
 * Returns the sx for a single day cell — today gets a dark blue tint.
 * @param {boolean} today_
 */
const cellSx = (today_) => ({
    position: 'relative',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    // CHANGED — iets donkerder dan accent #66b2ff
    bgcolor: today_ ? '#0d3560' : 'transparent',
});

/**
 * Returns the sx for the day number text — today gets the accent color for contrast.
 * @param {boolean} today_
 */
const numberSx = (today_) => ({
    fontSize: 11,
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: today_ ? 700 : 400,
    // FIXED — was #0b1e2e (donker navy) op #0d3560 bg, bijna onleesbaar.
    // #66b2ff — Jerny accent/licht blauw, zelfde als .fr-card--selected
    // .fr-title in YourNotesRow.css, voor actieve/highlighted tekst.
    color: today_ ? '#66b2ff' : '#b0c8e0',
    lineHeight: 1,
});

const eventDotSx = {
    position: 'absolute',
    bottom: 2,
    width: 4,
    height: 4,
    borderRadius: '50%',
    // Zelfde groen als "canSave" in EditEventDialog — bestaand
    // positief/success accent in deze codebase, geen nieuwe kleur.
    bgcolor: '#4caf8a',
};

/**
 * Single month grid — readonly, today highlighted.
 * Days with a CalEvent get a small green dot under the day number, plus a
 * hover tooltip listing "HH:MM - label" for each event that day (label
 * truncated to 45 chars), under a standard Jerny titlebar with the day's
 * date. Dot instead of a background tint, so it never visually fights with
 * the today highlight when both land on the same day.
 * Uses tooltipSxElevated (not tooltipSx) — this grid is always rendered
 * inside CalendarPopup, which sets zIndex: 10000 on itself. A tooltip with
 * the default z-index would mount behind that popup, invisible.
 * @param {object} props
 * @param {number} props.year
 * @param {number} props.month - 0-based
 * @param {Date} props.today
 * @param {CalEventObj[]} [props.events] - events to mark on the grid, default none
 */
export default function MonthGrid({ year, month, today, events = [] }) {
    const cells = buildDayCells(year, month);

    return (
        <Box sx={{ minWidth: 196 }}>
            {/* Month + year header */}
            <Typography sx={{
                color: '#a3d1ff',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "'DM Sans', sans-serif",
                mb: '6px',
                textAlign: 'center',
                letterSpacing: '0.04em',
            }}>
                {MONTH_NAMES[month]} {year}
            </Typography>

            {/* Day name headers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: '4px' }}>
                {DAY_NAMES.map((d) => (
                    <Typography key={d} sx={{
                        // CHANGED — #294766 is border color, onleesbaar als tekst
                        color: '#a3d1ff',
                        fontSize: 10,
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        textAlign: 'center',
                    }}>
                        {d}
                    </Typography>
                ))}
            </Box>

            {/* Day cells */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                {cells.map((d, i) => {
                    const today_ = isToday(d, year, month, today);
                    const dayEvents = getEventsForDay(d, year, month, events);

                    if (dayEvents.length === 0) {
                        return (
                            <Box key={i} sx={cellSx(today_)}>
                                {d !== null && <Typography sx={numberSx(today_)}>{d}</Typography>}
                            </Box>
                        );
                    }

                    return (
                        <Tooltip
                            key={i}
                            title={<DayEventsTooltip year={year} month={month} day={d} events={dayEvents} />}
                            componentsProps={tooltipSxElevated}
                        >
                            <Box sx={cellSx(today_)}>
                                <Typography sx={numberSx(today_)}>{d}</Typography>
                                <Box sx={eventDotSx} />
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
}
